import { Bot, ChevronLeft, ChevronRight, Eye, RefreshCw, Search, SlidersHorizontal, UserCheck } from "lucide-react";
import { formatDateTime, safeText } from "../../lib/formatters";
import { PROCESSING_STATUSES } from "../../lib/statuses";
import { Stars, StatusBadge } from "../../components/ui/Badge";
import { Button, IconButton } from "../../components/ui/Button";
import { EmptyState, ErrorState, Skeleton } from "../../components/ui/Feedback";
import { SelectInput, TextInput } from "../../components/ui/Form";
import { inquiryEmailIdentity } from "../../lib/inquiryEmail";

const SOP_SEGMENT_META = {
  A: { label: "SOP-A", description: "企业主体、规模、联系人和独立证据均已充分核验" },
  B: { label: "SOP-B", description: "企业主体及业务类型已核实，部分证据仍待补全" },
  C: { label: "SOP-C", description: "需求有效或企业已识别，企业证据仍待核实" },
  D: { label: "SOP-D", description: "存在已绑定客户主体的确认风险证据" },
  U: { label: "SOP-U", description: "当前信息不足，尚未形成企业背调分层" },
};

function inquirySopSegment(value) {
  const segment = String(value || "U").trim().toUpperCase();
  return Object.hasOwn(SOP_SEGMENT_META, segment) ? segment : "U";
}

export default function InquiryListPane({
  items, total, page, pageSize, loading, error, filters, selectedId,
  onFilters, onPage, onSelect, onSync, syncing, onRetry, onRating,
  selectedRows, onToggleRow, onTogglePage, onBulkProcess, bulkBusy,
  onPrefetch, onCancelPrefetch,
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const allSelected = Boolean(items.length) && selectedRows?.size === items.length;
  return (
    <section className="inquiry-list-pane">
      <header className="pane-header">
        <div><h2>询盘队列</h2><small>{total} 封 · 每页 {pageSize} 条</small></div>
        <IconButton label="同步邮箱" icon={RefreshCw} onClick={onSync} loading={syncing} />
      </header>
      <div className="inquiry-filters">
        <label className="search-control">
          <Search size={16} />
          <TextInput value={filters.q} onChange={(event) => onFilters({ q: event.target.value })} placeholder="客户、邮箱或产品" aria-label="搜索询盘" />
        </label>
        <SelectInput value={filters.status} onChange={(event) => onFilters({ status: event.target.value })} aria-label="状态筛选">
          <option value="">全部状态</option>
          <option value="new">待处理</option>
          <option value="ai_queued">AI 排队中</option>
          <option value="translating">中文翻译中</option>
          <option value="researching">客户背调中</option>
          <option value="generating_reply">回复生成中</option>
          <option value="send_queued">等待发送</option>
          <option value="sending">邮件发送中</option>
          <option value="pending_review">待审核</option>
          <option value="replied">已回复</option>
          <option value="ignored">无需回复</option>
          <option value="failed">处理失败</option>
        </SelectInput>
        <SelectInput value={filters.rating} onChange={(event) => onFilters({ rating: event.target.value })} aria-label="商业价值星级筛选">
          <option value="">全部星级</option>
          <option value="high">优质客户</option>
          <option value="5">5 星</option>
          <option value="4">4 星</option>
          <option value="3">3 星</option>
          <option value="2">2 星</option>
          <option value="1">1 星</option>
          <option value="unrated">未评估</option>
        </SelectInput>
        <details className="date-filter">
          <summary><SlidersHorizontal size={15} />时间</summary>
          <div>
            <label>开始日期<input type="date" value={filters.start_date} onChange={(event) => onFilters({ start_date: event.target.value })} /></label>
            <label>结束日期<input type="date" value={filters.end_date} onChange={(event) => onFilters({ end_date: event.target.value })} /></label>
          </div>
        </details>
      </div>
      <div className={`bulk-toolbar ${selectedRows?.size ? "is-active" : ""}`}>
        <label>
          <input type="checkbox" checked={allSelected} onChange={onTogglePage} />
          <span>{selectedRows?.size ? `已选 ${selectedRows.size} 封` : "选择本页"}</span>
        </label>
        {selectedRows?.size ? (
          <Button size="sm" variant="ghost" icon={Bot} loading={bulkBusy} onClick={onBulkProcess}>批量处理</Button>
        ) : <small>按收件时间倒序</small>}
      </div>
      <div className="inquiry-list-scroll">
        {loading && !items.length ? <Skeleton rows={8} /> : error ? <ErrorState compact message={error} onRetry={onRetry} /> : !items.length ? (
          <EmptyState title="没有匹配的询盘" description="调整筛选条件，或同步邮箱获取新邮件。" />
        ) : items.map((item) => {
          const { customerEmail, relayEmail } = inquiryEmailIdentity(item);
          const sopSegment = inquirySopSegment(item.sop_segment);
          const sopMeta = SOP_SEGMENT_META[sopSegment];
          return (
          <article
            className={`inquiry-row ${Number(selectedId) === Number(item.id) ? "is-selected" : ""}`}
            key={item.id}
            onClick={() => onSelect(item)}
            onMouseEnter={() => onPrefetch?.(item)}
            onMouseLeave={() => onCancelPrefetch?.(item)}
            onFocus={() => onPrefetch?.(item)}
            onBlur={() => onCancelPrefetch?.(item)}
            onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onSelect(item); }}
            role="button"
            tabIndex={0}
          >
            <label className="inquiry-row__check-wrap" onClick={(event) => event.stopPropagation()}>
              <input
                className="inquiry-row__check"
                type="checkbox"
                checked={selectedRows?.has(Number(item.id)) || false}
                onChange={() => onToggleRow(item.id)}
                aria-label={`选择 ${safeText(item.customer_name || item.sender_name, "该询盘")}`}
              />
            </label>
            <span className="inquiry-row__top">
              <span className="inquiry-row__identity">
                <strong className="truncate">{safeText(item.customer_name || item.sender_name, "未知客户")}</strong>
                {item.country && item.country !== "—" ? <small className="inquiry-row__country truncate">{item.country}</small> : null}
              </span>
              <time>{formatDateTime(item.received_at)}</time>
            </span>
            <span className="inquiry-row__email-line">
              <span className="inquiry-row__email truncate" title={customerEmail}>{safeText(customerEmail)}</span>
              {relayEmail ? <span className="inquiry-row__relay truncate" title={`中转邮箱：${relayEmail}`}>中转：{relayEmail}</span> : null}
            </span>
            <span className="inquiry-row__subject line-clamp-2">{safeText(item.product || item.subject, "需求待确认")}</span>
            {item.crm_owner?.owner_name ? (
              <span className="inquiry-row__crm" title={`富通历史档案负责人：${item.crm_owner.owner_name}。邮箱精确匹配，不代表已经实际沟通；请在 AI 分析的 CRM 面板核对。`}>
                <UserCheck size={14} aria-hidden="true" /><span className="truncate">富通负责人：{item.crm_owner.owner_name}</span>
              </span>
            ) : item.crm_owner?.status === "ambiguous" ? (
              <span className="inquiry-row__crm is-pending">富通匹配 {item.crm_owner.candidate_count} 个档案 · 待核对负责人</span>
            ) : item.crm_owner?.status === "archived" ? (
              <span className="inquiry-row__crm is-pending">富通历史档案已归档 · 待核对</span>
            ) : item.crm_owner?.status === "unavailable" ? (
              <span className="inquiry-row__crm is-pending">富通负责人暂未加载</span>
            ) : null}
            <span className="inquiry-row__meta">
              <span className="inquiry-row__rating">
                <Stars value={item.business_value_rating} editable onChange={(rating) => onRating(item, rating)} />
                <span
                  className={`inquiry-row__sop is-${sopSegment.toLowerCase()}`}
                  title={`客户 SOP 分层：${sopMeta.label}。${sopMeta.description}；该分层不替代五星商业价值。`}
                >
                  {sopMeta.label}
                </span>
              </span>
              <StatusBadge status={PROCESSING_STATUSES.has(item.status) ? item.status : item.status} label={item.status_label} />
              {item.status === "failed" ? (
                <Button size="sm" variant="ghost" icon={RefreshCw} onClick={(event) => { event.stopPropagation(); onRetry(item); }}>重试</Button>
              ) : <Eye size={15} />}
            </span>
          </article>
          );
        })}
      </div>
      <footer className="pane-pagination">
        <IconButton label="上一页" icon={ChevronLeft} disabled={page <= 1} onClick={() => onPage(page - 1)} />
        <span>第 <strong>{page}</strong> / {pages} 页</span>
        <IconButton label="下一页" icon={ChevronRight} disabled={page >= pages} onClick={() => onPage(page + 1)} />
      </footer>
    </section>
  );
}
